/**
 * ContactPage Component
 *
 * Public contact form allowing visitors to send messages to the admin.
 * Includes a simple math CAPTCHA to prevent spam.
 *
 * Features:
 * - Name, email, subject (dropdown), and message fields
 * - File attachment support (max 5MB) - only for logged-in users
 * - Math CAPTCHA (simple addition/subtraction)
 * - Native HTML5 validation
 * - Rate limiting awareness
 * - Success/error feedback
 * - Auto-fill email for logged-in users (read-only)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useStore } from '../store';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Predefined subject options
const SUBJECT_OPTIONS = [
  { value: '', label: 'Sélectionnez un sujet...' },
  { value: 'bug', label: 'Détection d\'anomalie' },
  { value: 'feature', label: 'Demande d\'évolution' },
  { value: 'support', label: 'Question / Assistance' },
  { value: 'feedback', label: 'Retour d\'expérience' },
  { value: 'other', label: 'Autre' },
];

// Max file size: 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

interface CaptchaChallenge {
  question: string;
  answer: number;
}

/**
 * Generate a simple math CAPTCHA
 */
function generateCaptcha(): CaptchaChallenge {
  const operations = ['+', '-'];
  const operation = operations[Math.floor(Math.random() * operations.length)];
  
  let num1: number, num2: number, answer: number;
  
  if (operation === '+') {
    num1 = Math.floor(Math.random() * 20) + 1; // 1-20
    num2 = Math.floor(Math.random() * 20) + 1; // 1-20
    answer = num1 + num2;
  } else {
    num1 = Math.floor(Math.random() * 20) + 10; // 10-30
    num2 = Math.floor(Math.random() * num1); // 0 to num1 (ensure positive result)
    answer = num1 - num2;
  }
  
  return {
    question: `${num1} ${operation} ${num2} = ?`,
    answer,
  };
}

export function ContactPage() {
  // Get current user from store
  const user = useStore((state) => state.user);
  const isAuthenticated = useStore((state) => state.isAuthenticated);
  const isLoggedIn = isAuthenticated && !!user?.email;

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [attachment, setAttachment] = useState<File | null>(null);
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState<CaptchaChallenge>(() => generateCaptcha());
  
  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Refs for validation
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captchaInputRef = useRef<HTMLInputElement>(null);

  // Pre-fill email for logged-in users
  useEffect(() => {
    if (isLoggedIn && user?.email) {
      setEmail(user.email);
    }
  }, [isLoggedIn, user?.email]);

  // Regenerate CAPTCHA
  const refreshCaptcha = useCallback(() => {
    setCaptcha(generateCaptcha());
    setCaptchaInput('');
    // Clear any custom validity
    if (captchaInputRef.current) {
      captchaInputRef.current.setCustomValidity('');
    }
  }, []);

  // Reset form after successful submission
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setAttachment(null);
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setError('Le fichier est trop volumineux (maximum 5 Mo).');
      e.target.value = '';
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setError('Type de fichier non autorisé. Formats acceptés : images, PDF, documents Word, texte.');
      e.target.value = '';
      return;
    }

    setError(null);
    setAttachment(file);
  };

  // Remove attachment
  const removeAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} o`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  // Validate CAPTCHA and set custom validity
  const validateCaptcha = useCallback((value: string) => {
    if (!captchaInputRef.current) return false;
    
    if (!value.trim()) {
      captchaInputRef.current.setCustomValidity('Veuillez répondre au calcul anti-spam.');
      return false;
    }
    
    const captchaValue = parseInt(value, 10);
    if (isNaN(captchaValue) || captchaValue !== captcha.answer) {
      captchaInputRef.current.setCustomValidity('La réponse au calcul est incorrecte.');
      return false;
    }
    
    captchaInputRef.current.setCustomValidity('');
    return true;
  }, [captcha.answer]);

  // Handle CAPTCHA input change
  const handleCaptchaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    setCaptchaInput(value);
    validateCaptcha(value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Validate CAPTCHA before submit (only for non-logged-in users)
    if (!isLoggedIn && !validateCaptcha(captchaInput)) {
      captchaInputRef.current?.reportValidity();
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the subject label for display in email
      const subjectLabel = SUBJECT_OPTIONS.find(opt => opt.value === subject)?.label || subject;
      
      // Build FormData for multipart upload
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('email', email.trim());
      formData.append('subject', subjectLabel);
      formData.append('message', message.trim());
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        if (response.status === 429) {
          setError('Trop de messages envoyés. Veuillez attendre quelques minutes avant de réessayer.');
        } else {
          setError(data.error?.message || 'Une erreur est survenue. Veuillez réessayer.');
        }
        refreshCaptcha();
        return;
      }

      // Success
      setSuccess(true);
      setName('');
      // Keep email if user is logged in
      if (!isLoggedIn) {
        setEmail('');
      }
      setSubject('');
      setMessage('');
      setCaptchaInput('');
      setAttachment(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refreshCaptcha();
    } catch {
      setError('Erreur de connexion. Veuillez vérifier votre connexion internet.');
      refreshCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-display text-3xl font-bold text-slate-900 dark:text-white mb-2">
          Nous contacter
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Une question, une suggestion ou un problème ? Envoyez-nous un message.
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-400">
          <div className="flex items-start gap-3">
            <span className="text-green-500 dark:text-green-400 text-xl">✓</span>
            <div>
              <p className="font-medium">Message envoyé avec succès !</p>
              <p className="text-sm mt-1">Nous vous répondrons dans les plus brefs délais.</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message - Only for server errors */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-700 dark:text-red-400">
          <div className="flex items-start gap-3">
            <span className="text-red-500 dark:text-red-400 text-xl">✗</span>
            <p>{error}</p>
          </div>
        </div>
      )}

      {/* Contact Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-700 p-6 space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Nom <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            maxLength={100}
            className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50 dark:disabled:bg-slate-800"
            placeholder="Votre nom"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Email <span className="text-red-500">*</span>
            {isLoggedIn && (
              <span className="text-slate-400 dark:text-slate-500 font-normal ml-2">(email de votre compte)</span>
            )}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            required
            value={email}
            onChange={isLoggedIn ? undefined : (e) => setEmail(e.target.value)}
            disabled={isSubmitting}
            readOnly={isLoggedIn}
            maxLength={255}
            className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 ${
              isLoggedIn 
                ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 cursor-not-allowed border-slate-200 dark:border-slate-600' 
                : 'border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white'
            }`}
            placeholder="votre@email.com"
          />
        </div>

        {/* Subject */}
        <div>
          <label htmlFor="subject" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Sujet <span className="text-red-500">*</span>
          </label>
          <select
            id="subject"
            name="subject"
            required
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSubmitting}
            className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50 dark:disabled:bg-slate-800 bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          >
            {SUBJECT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="message" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Message <span className="text-red-500">*</span>
            <span className="text-slate-400 dark:text-slate-500 font-normal ml-1">(20 caractères minimum)</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            minLength={20}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isSubmitting}
            rows={5}
            maxLength={5000}
            className="w-full px-4 py-2.5 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:bg-slate-50 dark:disabled:bg-slate-800 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
            placeholder="Décrivez votre question ou remarque..."
          />
          <div className="flex justify-end text-xs mt-1">
            <span className="text-slate-400 dark:text-slate-500">{message.length}/5000</span>
          </div>
        </div>

        {/* Attachment - Only for logged-in users */}
        {isLoggedIn && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Pièce jointe <span className="text-slate-400 dark:text-slate-500 font-normal">(optionnel, max 5 Mo)</span>
            </label>
            <div className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-4 bg-slate-50 dark:bg-slate-700">
              {attachment ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <AttachmentIcon className="w-8 h-8 text-primary-500 dark:text-primary-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate">{attachment.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{formatFileSize(attachment.size)}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeAttachment}
                    disabled={isSubmitting}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                    title="Supprimer la pièce jointe"
                  >
                    <CloseIcon className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center cursor-pointer">
                  <UploadIcon className="w-10 h-10 text-slate-400 dark:text-slate-500 mb-2" />
                  <span className="text-sm text-slate-600 dark:text-slate-400">Cliquez pour sélectionner un fichier</span>
                  <span className="text-xs text-slate-400 dark:text-slate-500 mt-1">Images, PDF, Word, Texte</span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt,.doc,.docx"
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>
        )}

        {/* CAPTCHA - Only for non-logged-in users */}
        {!isLoggedIn && (
          <div className="bg-slate-50 dark:bg-slate-700 rounded-lg p-4">
            <label htmlFor="captcha" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Vérification anti-spam <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-3">
              <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 font-mono text-lg font-bold text-slate-700 dark:text-slate-300 select-none">
                {captcha.question}
              </div>
              <input
                ref={captchaInputRef}
                type="text"
                id="captcha"
                name="captcha"
                required
                inputMode="numeric"
                value={captchaInput}
                onChange={handleCaptchaChange}
                disabled={isSubmitting}
                maxLength={3}
                className="w-20 px-3 py-2 border border-slate-200 dark:border-slate-600 rounded-lg text-center font-mono text-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                placeholder="?"
              />
              <button
                type="button"
                onClick={refreshCaptcha}
                disabled={isSubmitting}
                className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-50"
                title="Nouveau calcul"
              >
                <RefreshIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <LoadingSpinner className="w-5 h-5" />
              Envoi en cours...
            </>
          ) : (
            <>
              <SendIcon className="w-5 h-5" />
              Envoyer le message
            </>
          )}
        </button>
      </form>

      {/* Additional Info */}
      <div className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
        <p>Nous répondons généralement sous 24 à 48 heures.</p>
      </div>
    </div>
  );
}

// Icons
function RefreshIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

function SendIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
    </svg>
  );
}

function LoadingSpinner({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function AttachmentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

export default ContactPage;
